import {
  Button,
  ButtonGroup,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  Table,
  useDefaultSelecting,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useConfirm } from '../../../hooks/useConfirm';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useTimetableVersionDataQuery } from '../../../types/planung-graphql-client-defs';
import { hexToColorOption } from '../../../utils/colorUtils';
import { useTimetableStore } from '../../TimetableGrid/TimetableProvider';
import { TimetableVersionTableType } from '../graphql/types';
import { createTimetableVersionTableColumns } from '../utils/timetableVersionTableUtils';

type TimetableVersionSubjectsTableProps = {
  versionUuid: string;
  onRowSelection: (rowData: TimetableVersionTableType, index: number) => void;
  onShowLessonClick: (uuid: string) => void;
  versionSubjectUuids: string[];
  search: string;
  onRemove: (uuids: string[]) => void;
};

export const TimetableVersionSubjectsTable = observer(
  ({
    versionUuid,
    onRowSelection,
    onShowLessonClick,
    versionSubjectUuids,
    search,
    onRemove,
  }: TimetableVersionSubjectsTableProps) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const store = useTimetableStore();

    const { sorting, saveSorting } = useColumnsSort('timetableVersionSubjectsTable');
    const { rowSelection, onRowSelectionChange } = useDefaultSelecting({ 0: true });

    const columns = useMemo(() => createTimetableVersionTableColumns(t('subject.title', { count: 1 })), [t]);

    const [{ data: versionData }] = useTimetableVersionDataQuery({
      variables: { versionUuid },
      pause: !versionUuid,
    });
    const subjectsView = versionData?.timetableVersionData.view.subjects;
    const editorLessons = versionData?.timetableVersionData.editor?.lessons;

    const subjects: TimetableVersionTableType[] = useMemo(() => {
      return (
        subjectsView
          ?.filter((s) => versionSubjectUuids.includes(s.uuid))
          .map((s) => {
            const { html, label } = hexToColorOption(s.color ?? '');

            return {
              color: {
                color: html,
                label,
              },
              count: editorLessons?.filter((l) => l.subjectUuid === s.uuid).length ?? 0,
              name: s.name,
              shortName: s.shortName ?? '',
              uuid: s.uuid,
            };
          }) ?? []
      );
    }, [subjectsView, versionSubjectUuids, editorLessons]);

    const canBeRemoved = (uuids: string[]) => {
      return !editorLessons?.some((l) => uuids.includes(l.subjectUuid));
    };

    const { confirm, ConfirmationDialog } = useConfirm();

    const handleRemove = async (uuids: string[]) => {
      const res = await confirm({
        title: t('timetableVersion.removeSubjects', { count: uuids.length }),
        message: t('timetableVersion.removeSubjectsDescription', { count: uuids.length }),
      });
      if (res) {
        onRemove(uuids);
      }
    };
    const readOnly = store.readonly;

    return (
      <>
        <Table<TimetableVersionTableType>
          isDesktop={!isMobile}
          showBorderRadius
          showShadow
          canScroll
          minHeight={900}
          sorting={sorting}
          onSortingChange={saveSorting}
          columns={columns}
          data={subjects}
          globalFilter={search}
          lastColWidth='140px'
          lastCol={(row) => {
            return (
              <ButtonGroup>
                <Button hierarchy='secondary' onClick={() => onShowLessonClick(row.original.uuid)}>
                  {t('timetableVersion.showLessons')}
                </Button>
                <Dropdown
                  noPadding
                  trigger={<Button hierarchy='secondary' icon={<DotsHorizontalIcon className='small' />} />}
                >
                  <DropdownMenu
                    data={[
                      {
                        label: t('timetableVersion.editData'),
                        disabled: true,
                        onClick: () => {
                          console.log('edit clicked');
                        },
                      },
                      {
                        label: t('timetableVersion.test'),
                        disabled: true,
                      },
                      {
                        label: t('timetableVersion.remove'),
                        type: 'default',
                        color: 'error',
                        disabled: !canBeRemoved([row.original.uuid]) || readOnly,
                        onClick: async () => {
                          await handleRemove([row.original.uuid]);
                        },
                      },
                    ]}
                  />
                </Dropdown>
              </ButtonGroup>
            );
          }}
          showRowHover
          onRowClick={(event, row) => {
            if (onRowSelectionChange) onRowSelectionChange({ [row.id]: true });
            onRowSelection(row.original, row.index);
            document.querySelector('#content')?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onRowSelectionChange={onRowSelectionChange}
          rowSelection={rowSelection}
          showSelected
        />
        <ConfirmationDialog />
      </>
    );
  },
);
